//
// A named group of fields inside a form. Written once because the app's forms were flat stacks of
// every control a payload happens to carry: a dialog of eleven inputs with no heading anywhere in
// its body reads as a list of things to fill in rather than as the decision the operator is making.
// The shape here is the one `CreateAutomationKeyDialog` already established — a `subtitle2` heading
// in the user's words, an optional one-line explanation, then the group's fields in one column.
//
// It deliberately paints no card and draws no border: a section lives *inside* a dialog body or a
// card that already owns the surface, and a second frame around it would read as a nested box.
// Sections are separated by their host's own `Stack spacing={3}`.
//
// The heading is the group's accessible label (`role="group"` + `aria-labelledby`), so a screen
// reader announces "A qué afecta, group" on entering it instead of reading five unrelated fields.

import React, { useId } from 'react';
import { Box, Stack, Typography } from '@mui/material';

export interface FormSectionProps {
  /** Named in the user's words ("Cuándo", "La persona"), never after the schema. A numbered title
   *  ("1 · El conector") is just this string. */
  title: string;
  /** One line explaining the group, when the fields alone do not say why they are together. */
  description?: React.ReactNode;
  /** The group's fields, one per line (a `FieldPair` counts as one line). */
  children: React.ReactNode;
  /** Tightens the gap between fields from 2 to 1.5 — for a group of short controls inside a
   *  dialog, where the standard rhythm makes the body scroll for no reason. */
  dense?: boolean;
  /** One control belonging to the group as a whole (a link, a small button), on the heading line.
   *  Added so a section that owns an action does not have to hand-roll its own heading row. */
  action?: React.ReactNode;
}

export const FormSection: React.FC<FormSectionProps> = ({ title, description, children, dense = false, action }) => {
  const headingId = useId();

  return (
    // Full width: react-admin's SimpleForm lays its children out with `alignItems: flex-start`, so a
    // section sized to its content and a `fullWidth` field inside it stopped at its intrinsic width.
    <Box role="group" aria-labelledby={headingId} sx={{ width: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography id={headingId} variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      )}
      <Stack spacing={dense ? 1.5 : 2} sx={{ mt: 1.5 }}>
        {children}
      </Stack>
    </Box>
  );
};

export default FormSection;
